import React from 'react';
import { UserLanguage, UserSettings } from '../api';
import { TELEGRAM_BOT_URL } from '../utils/telegram';

interface PlayerSettingsViewProps {
  playerName: string;
  language: UserLanguage;
  sendNotificationsToTelegram: boolean;
  telegramId?: string | null;
  sendNotificationsToEmail: boolean;
  email?: string | null;
  saving: boolean;
  saved?: boolean;
  error: string | null;
  onSettingsChange: (settings: Partial<UserSettings>, debounce?: boolean) => void;
  onSettingsFlush: () => Promise<boolean>;
  onSettingsRetry: () => Promise<boolean>;
  onLogout: () => Promise<void>;
  onClose: () => void;
  inPopup?: boolean;
}

export const PlayerSettingsView: React.FC<PlayerSettingsViewProps> = ({
  playerName,
  language,
  sendNotificationsToTelegram,
  telegramId,
  sendNotificationsToEmail,
  email,
  saving,
  saved,
  error,
  onSettingsChange,
  onSettingsFlush,
  onSettingsRetry,
  onLogout,
  onClose,
  inPopup = false,
}) => {
  const selectedLanguage = language;
  const telegramEnabled = sendNotificationsToTelegram;
  const telegramIdValue = telegramId ?? '';
  const emailEnabled = sendNotificationsToEmail;
  const emailValue = email ?? '';

  const normalizeTelegramId = (value: string) => value.trim();
  const normalizeEmail = (value: string) => value.trim();
  const isEmailWithAtInMiddle = (value: string) => {
    const atIndex = value.indexOf('@');
    return atIndex > 0 && atIndex < value.length - 1;
  };
  const isDigitsOnly = (value: string) => /^\d+$/.test(value);

  const normalizedTelegramIdValue = normalizeTelegramId(telegramIdValue);
  const normalizedEmailValue = normalizeEmail(emailValue);
  const isTelegramIdValid = normalizedTelegramIdValue.length === 0 || isDigitsOnly(normalizedTelegramIdValue);
  const isEmailValid = normalizedEmailValue.length === 0 || isEmailWithAtInMiddle(normalizedEmailValue);

  return (
    <div className={`player-settings-view ${inPopup ? 'player-settings-view-modal' : ''}`}>
      <div className="player-settings-card">
        {!inPopup && <div className="player-settings-header">
          <h1>Account</h1>
          <button type="button" className="close-settings" onClick={onClose} aria-label="Close account">
            x
          </button>
        </div>}
        <div className="settings-row">
          <span className="settings-label">Player</span>
          <span className="settings-value">{playerName || 'Player'}</span>
        </div>

        <form className="settings-form" onSubmit={(event) => event.preventDefault()}>
          {false && <div className="settings-field-language">
            <label className='language-label' htmlFor="language-select">
              Language
            </label>
            <select
              id="language-select"
              className="select-input select-input-language"
              value={selectedLanguage}
              onChange={(event) => onSettingsChange({ language: event.target.value as UserLanguage })}
            >
              <option value="En">En</option>
              <option value="Ru">Ru</option>
            </select>
          </div>}

          <div className="settings-field">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={telegramEnabled}
                onChange={(event) => onSettingsChange({ sendNotificationsToTelegram: event.target.checked })}
              />
              Send notifications to Telegram
            </label>
          </div>

          {telegramEnabled && (
            <div className="settings-field">
              <label htmlFor="telegram-id-input">Telegram ID</label>
              <input
                id="telegram-id-input"
                type="text"
                className="text-input"
                value={telegramIdValue}
                onChange={(event) => onSettingsChange({ telegramId: event.target.value.replace(/\D/g, '') }, true)}
                onBlur={() => { void onSettingsFlush(); }}
                inputMode="numeric"
              />
              {!isTelegramIdValid && <div className="error">Telegram ID must contain only digits</div>}
              <div className="telegram-notifications-hint">To get ID and start receiving notifications, open <a href={TELEGRAM_BOT_URL} target='_blank' rel="noreferrer">this bot</a> and press Start</div>
            </div>
          )}

          {false && <><div className="settings-field">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={emailEnabled}
                onChange={(event) => onSettingsChange({ sendNotificationsToEmail: event.target.checked })}
              />
              Send notifications to Email
            </label>
          </div>

          {emailEnabled && (
            <div className="settings-field">
              <label htmlFor="email-input">Email</label>
              <input
                id="email-input"
                type="text"
                className="text-input"
                value={emailValue}
                onChange={(event) => onSettingsChange({ email: event.target.value }, true)}
                onBlur={() => { void onSettingsFlush(); }}
              />
              {!isEmailValid && <div className="error">Email must contain @ in the middle</div>}
            </div>
          )}</>}

          <div className="settings-save-status" role="status" aria-live="polite">
            {saving ? 'Saving…' : saved ? 'Saved.' : ''}
          </div>
        </form>

        {error && <div className="error" role="alert">{error} <button type="button" onClick={() => { void onSettingsRetry(); }}>Retry</button></div>}

        <button type="button" className="logout-button" onClick={async () => {
          if (await onSettingsFlush()) await onLogout();
        }}>
          Logout
        </button>
      </div>
    </div>
  );
};
